import {
  type GetActiveTimeTrackingEntriesQuery,
  timeTrackingService,
} from '@/api/time-tracking.service';
import type { TimeTrackingEntry } from '@/entities/time-tracking/time-tracking.entity';
import type { ClockInDto, ClockOutDto } from '@carvia/ros-client-types';
import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/vue-query';
import { type MaybeRefOrGetter } from '@vueuse/core';
import { toValue } from 'vue';

const QUERY_KEY = 'time-tracking';

export const useActiveTimeTrackingEntries = (
  query?: MaybeRefOrGetter<GetActiveTimeTrackingEntriesQuery | undefined>,
  enabled: MaybeRefOrGetter<boolean> = true,
  options?: Omit<UseQueryOptions<TimeTrackingEntry[]>, 'queryKey' | 'queryFn' | 'enabled'>,
) => {
  return useQuery({
    queryKey: [QUERY_KEY, 'active-entries', query],
    queryFn: () => timeTrackingService.getActiveEntries(toValue(query)),
    enabled,
    ...options,
  });
};

export const useMyActiveTimeTrackingEntry = (enabled: MaybeRefOrGetter<boolean> = true) => {
  return useQuery({
    queryKey: [QUERY_KEY, 'active-entry', 'me'],
    queryFn: () => timeTrackingService.getMyActiveEntry(),
    enabled,
  });
};

export const useClockOutMyActiveTimeTrackingEntry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [QUERY_KEY, 'active-entry', 'me', 'clock-out'],
    mutationFn: (dto: ClockOutDto) => timeTrackingService.clockOutMyActiveEntry(dto),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEY, 'active-entry', 'me'] });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEY, 'active-entries'] });
    },
  });
};

export const useClockInMyActiveTimeTrackingEntry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [QUERY_KEY, 'active-entry', 'me', 'clock-in'],
    mutationFn: (dto: ClockInDto) => timeTrackingService.clockInMyActiveEntry(dto),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEY, 'active-entry', 'me'] });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEY, 'active-entries'] });
    },
  });
};
